/**
 * Estimates the offset between this client's `performance.now()` and the
 * server's simulation timeline.
 *
 * Everything the renderer does with remote entities depends on this being
 * both accurate and *stable*. The lessons doc's warning applies directly: one
 * delayed pong must not jerk the shared timeline, so samples are folded in
 * with an EMA rather than adopted.
 */

import { CLOCK_EMA_ALPHA, RTT_EMA_ALPHA, ema } from "@prop-hunt/shared";

export class ClockSync {
  private offsetMs = 0;
  private rttMs = 0;
  private sampleCount = 0;

  /**
   * @param clientSendTime `performance.now()` when the ping went out
   * @param serverTimeMs   the server's simulation time in its reply
   * @param now            `performance.now()` on arrival
   */
  onPong(clientSendTime: number, serverTimeMs: number, now: number): void {
    const rtt = now - clientSendTime;

    // Assume the path is symmetric: the server's stamp was current half an
    // RTT ago. Asymmetric routes bias this, but consistently — and a constant
    // bias is invisible, because every client interpolates against its own
    // estimate of the same timeline.
    const sample = serverTimeMs + rtt / 2 - now;

    if (this.sampleCount === 0) {
      // Nothing to smooth against yet; adopt the first sample outright rather
      // than easing away from a meaningless zero.
      this.offsetMs = sample;
      this.rttMs = rtt;
    } else {
      this.offsetMs = ema(this.offsetMs, sample, CLOCK_EMA_ALPHA);
      this.rttMs = ema(this.rttMs, rtt, RTT_EMA_ALPHA);
    }
    this.sampleCount += 1;
  }

  /**
   * Forget every sample, for a session that is starting over.
   *
   * A reconnect is not a continuation of the old timeline. The lobby it lands
   * on may be a revived Durable Object whose tick counter restarted at zero
   * (see lobby.ts), in which case server time has jumped *backwards* — and an
   * EMA is precisely the wrong tool for that: it would spend ten seconds
   * walking to the new offset while every remote entity is interpolated
   * against a timeline that does not exist. Dropping to zero samples makes the
   * first pong of the new session authoritative again, and `ready` false in
   * the meantime, which is what stops anything being drawn from the old one.
   */
  reset(): void {
    this.offsetMs = 0;
    this.rttMs = 0;
    this.sampleCount = 0;
  }

  /** Server simulation time corresponding to a local `performance.now()`. */
  serverNow(now: number): number {
    return now + this.offsetMs;
  }

  get rtt(): number {
    return this.rttMs;
  }

  get offset(): number {
    return this.offsetMs;
  }

  get ready(): boolean {
    return this.sampleCount > 0;
  }

  get samples(): number {
    return this.sampleCount;
  }
}
